/**
 * 🎨 FUN PAGE - People Pixels
 * 
 * NOW WITH: 🌪️ REAL PHYSICS + INERTIA!!!
 * Touch the box = YOU control it!
 * Spin it = It keeps spinning with INERTIA!
 * 
 * Built by Tom & Azura - January 14, 2026 ❤️⚡️❤️
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Gift, ChevronRight, ChevronLeft, Users, X, ZoomIn, Bell, BellRing, Calendar, Wifi, WifiOff } from 'lucide-react';
import { useUserStore } from '../store';
import {
  PixelConnection,
  createPixelConnection,
  ConnectionState,
} from '../lib/pixelConnection';
import {
  useCreaturePersonality,
  TassieDevil,
  AnxiousBird,
  SleepyBear,
  CuriousAlien,
  ChaoticGremlin,
  WiseOwl,
  PlayfulPuppy,
} from '../lib/creature-personality';
import type { Temperament } from '../lib/creature-personality';
import styles from './Fun.module.css';

// 🔧 DEV MODE FLAG
const DEV_MODE = false; // Set to true to show creature selector arrows

// Pixel server URL
const PIXEL_SERVER_URL = import.meta.env.PROD
  ? 'wss://people-pixels.truthconsultingint.workers.dev'
  : 'ws://localhost:8788';

// 🎭 AVAILABLE TEMPERAMENTS (for dev navigation)
const TEMPERAMENTS: { id: string; name: string; emoji: string; temperament: Temperament }[] = [
  { id: 'tassie-devil', name: 'Tassie Devil', emoji: '🌪️', temperament: TassieDevil },
  { id: 'anxious-bird', name: 'Anxious Bird', emoji: '🐦', temperament: AnxiousBird },
  { id: 'sleepy-bear', name: 'Sleepy Bear', emoji: '🐻', temperament: SleepyBear },
  { id: 'curious-alien', name: 'Curious Alien', emoji: '👽', temperament: CuriousAlien },
  { id: 'chaotic-gremlin', name: 'Chaotic Gremlin', emoji: '👹', temperament: ChaoticGremlin },
  { id: 'wise-owl', name: 'Wise Owl', emoji: '🦉', temperament: WiseOwl },
  { id: 'playful-puppy', name: 'Playful Puppy', emoji: '🐕', temperament: PlayfulPuppy },
];

interface PixelEvent {
  id: string;
  name: string;
  time: string;
  mode: 'stand_sit' | 'phone_up';
  reward: string;
  imagePreview: string;
}

const mockEvents: PixelEvent[] = [
  { id: 'evt1', name: 'Cowboys Logo', time: '7:45 PM', mode: 'phone_up', reward: '15% off next order', imagePreview: '🐴' },
  { id: 'evt2', name: 'Aussie Aussie Aussie!', time: '8:15 PM', mode: 'stand_sit', reward: 'Free topping', imagePreview: '🦘' },
  { id: 'evt3', name: 'Tropical Paradise', time: 'Halftime', mode: 'phone_up', reward: '$2 off', imagePreview: '🌴' },
];

// 🎮 PHYSICS CONSTANTS
const FRICTION = 0.96;           // How quickly rotation slows (0.9 = fast stop, 0.99 = slow stop)
const SENSITIVITY = 0.4;         // How much finger movement affects rotation
const MAX_VELOCITY = 25;         // Maximum angular velocity
const VELOCITY_THRESHOLD = 0.1;  // Below this, stop completely
const POKE_VELOCITY_THRESHOLD = 8; // Above this velocity = creature gets poked!

// 🌪️ AUTO-PROGRESSION CONSTANTS
const RETURN_TO_AUTO_DELAY = 3000;   // Time after interaction before returning to auto (3 seconds)

export function FunPage() {
  const navigate = useNavigate();
  const seatInfo = useUserStore((state) => state.seatInfo);

  // 🎭 Current temperament (for dev navigation)
  const [temperamentIndex, setTemperamentIndex] = useState(0);
  const currentTemperament = TEMPERAMENTS[temperamentIndex];

  // WebSocket state
  const [wsConnection, setWsConnection] = useState<PixelConnection | null>(null);
  const [wsState, setWsState] = useState<ConnectionState>('disconnected');
  const wsConnectedRef = useRef(false);

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [phoneColor, setPhoneColor] = useState<string | null>(null);
  const [participated, setParticipated] = useState(false);
  const [lastReward, setLastReward] = useState<string | null>(null);
  const [showBrightnessPrompt, setShowBrightnessPrompt] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [wakeLock, setWakeLock] = useState<any>(null);
  
  // Mystery Box state
  const [artRevealed, setArtRevealed] = useState(false);
  const [artRevealing, setArtRevealing] = useState(false);

  // Stadium lightbox state
  const [showStadiumLightbox, setShowStadiumLightbox] = useState(false);

  // 🌪️ CREATURE PERSONALITY SYSTEM ❤️⚡️❤️
  const {
    mood,
    energy,
    expression,
    bulge,
    indicators,
    triggerStimulus,
  } = useCreaturePersonality(currentTemperament.temperament, !artRevealed);

  // 🎮 PHYSICS STATE
  const boxRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [physicsRotation, setPhysicsRotation] = useState({ x: -20, y: -25, z: 0 });
  const [velocity, setVelocity] = useState({ x: 0, y: 0, z: 0 });
  const lastPointerRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const velocityHistoryRef = useRef<{ dx: number; dy: number; dt: number }[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const [interactionFeedback, setInteractionFeedback] = useState<string | null>(null);

  // 🌪️ AUTO-PROGRESSION STATE
  const [isUserControlled, setIsUserControlled] = useState(false); // User interacting = true
  const lastInteractionRef = useRef<number>(0);           // Timestamp of last user interaction

  // 🎭 RAGE ANIMATION PHASE STATE
  // 'intro' = squash down + spring up (3s, plays once)
  // 'shake' = mid-air shaking (loops)
  // 'outro' = Wile E. Coyote drop + land squash (0.5s, plays once)
  const [ragePhase, setRagePhase] = useState<'intro' | 'shake' | 'outro' | null>(null);
  const prevMoodRef = useRef<string | null>(null);

  // 🎭 RAGE PHASE MANAGEMENT
  const rageTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const prevMood = prevMoodRef.current;
    prevMoodRef.current = mood;

    // Clear any existing timer
    if (rageTimerRef.current) {
      clearTimeout(rageTimerRef.current);
      rageTimerRef.current = null;
    }

    // Entering rage from another mood
    if (mood === 'rage' && prevMood !== 'rage') {
      setRagePhase('intro');

      // After 1.5 seconds (intro duration), switch to shake phase
      rageTimerRef.current = setTimeout(() => {
        setRagePhase('shake');
      }, 1500);
      return;
    }

    // Exiting rage to exhausted - trigger outro
    if (prevMood === 'rage' && mood === 'exhausted') {
      setRagePhase('outro');

      // After 1.1 seconds (outro duration with Wile E. Coyote moment), clear phase
      rageTimerRef.current = setTimeout(() => {
        setRagePhase(null);
      }, 1100);
      return;
    }

    // If not in rage and not doing outro, clear phase
    if (mood !== 'rage' && ragePhase !== 'outro') {
      setRagePhase(null);
    }
  }, [mood]); // Only depend on mood, NOT ragePhase!

  // Show feedback briefly
  const showFeedback = useCallback((message: string) => {
    setInteractionFeedback(message);
    setTimeout(() => setInteractionFeedback(null), 500);
  }, []);

  // 🎮 PHYSICS: Pointer Down - Start dragging
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (artRevealed) return;

    // Track user interaction
    lastInteractionRef.current = Date.now();
    setIsUserControlled(true);

    setIsDragging(true);
    lastPointerRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    velocityHistoryRef.current = [];

    // Stop any existing velocity
    setVelocity({ x: 0, y: 0, z: 0 });

    // Capture pointer for smooth tracking
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [artRevealed]);

  // 🎮 PHYSICS: Pointer Move - Drag rotation
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !lastPointerRef.current || artRevealed) return;

    const now = Date.now();
    const dt = now - lastPointerRef.current.time;
    if (dt === 0) return;

    const dx = e.clientX - lastPointerRef.current.x;
    const dy = e.clientY - lastPointerRef.current.y;

    // Store velocity history (last 5 samples)
    velocityHistoryRef.current.push({ dx, dy, dt });
    if (velocityHistoryRef.current.length > 5) {
      velocityHistoryRef.current.shift();
    }

    // Update rotation based on finger movement
    // Horizontal movement = Y rotation, Vertical movement = X rotation
    setPhysicsRotation(prev => ({
      x: Math.max(-60, Math.min(30, prev.x - dy * SENSITIVITY)),
      y: prev.y + dx * SENSITIVITY,
      z: prev.z + (dx * 0.1), // Slight Z rotation for realism
    }));

    lastPointerRef.current = { x: e.clientX, y: e.clientY, time: now };
  }, [isDragging, artRevealed]);

  // 🎮 PHYSICS: Pointer Up - Release with velocity!
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    // Track user interaction end time
    lastInteractionRef.current = Date.now();

    setIsDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);

    // Calculate average velocity from history
    const history = velocityHistoryRef.current;
    if (history.length === 0) return;

    let totalDx = 0, totalDy = 0, totalDt = 0;
    history.forEach(h => {
      totalDx += h.dx;
      totalDy += h.dy;
      totalDt += h.dt;
    });

    if (totalDt === 0) return;

    // Convert to velocity (pixels per frame, roughly)
    const vx = (totalDy / totalDt) * -16 * SENSITIVITY; // Vertical movement = X rotation
    const vy = (totalDx / totalDt) * 16 * SENSITIVITY;  // Horizontal movement = Y rotation
    const vz = (totalDx / totalDt) * 2 * SENSITIVITY;   // Slight Z spin

    // Clamp velocities
    const clampedVelocity = {
      x: Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, vx)),
      y: Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, vy)),
      z: Math.max(-MAX_VELOCITY / 3, Math.min(MAX_VELOCITY / 3, vz)),
    };

    setVelocity(clampedVelocity);

    // Calculate total spin speed
    const spinSpeed = Math.sqrt(vx * vx + vy * vy);
    
    // Trigger creature reaction based on spin speed!
    if (spinSpeed > POKE_VELOCITY_THRESHOLD * 2) {
      showFeedback('🌪️ SPIN!');
      triggerStimulus('shake', Math.min(1, spinSpeed / 20));
    } else if (spinSpeed > POKE_VELOCITY_THRESHOLD) {
      showFeedback('👆 FLICK!');
      triggerStimulus('poke', 0.5);
    }

    lastPointerRef.current = null;
  }, [isDragging, triggerStimulus, showFeedback]);

  // 🎮 PHYSICS: Animation loop - Apply friction & inertia
  useEffect(() => {
    if (artRevealed) return;

    const animate = () => {
      setVelocity(prev => {
        // Apply friction
        const newVel = {
          x: prev.x * FRICTION,
          y: prev.y * FRICTION,
          z: prev.z * FRICTION,
        };

        // Stop if velocity is tiny
        const speed = Math.sqrt(newVel.x ** 2 + newVel.y ** 2 + newVel.z ** 2);
        if (speed < VELOCITY_THRESHOLD) {
          return { x: 0, y: 0, z: 0 };
        }

        return newVel;
      });

      // Update rotation based on velocity (only when not dragging)
      if (!isDragging) {
        setPhysicsRotation(prev => ({
          x: Math.max(-60, Math.min(-10, prev.x + velocity.x)), // Clamp to always view from top (never positive!)
          y: prev.y + velocity.y,
          z: prev.z * 0.95 + velocity.z, // Z slowly returns to 0
        }));
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, velocity, artRevealed]);

  // 🌪️ AUTO-PROGRESSION: Return to auto-mode after user stops interacting
  // Energy builds AUTOMATICALLY via the state machine's negative moodDrain values
  // This effect just handles the isUserControlled flag
  useEffect(() => {
    if (artRevealed) return;

    const checkAutoReturn = () => {
      const now = Date.now();
      const timeSinceInteraction = now - lastInteractionRef.current;

      // If user hasn't interacted recently, return to auto mode
      if (isUserControlled && timeSinceInteraction > RETURN_TO_AUTO_DELAY) {
        setIsUserControlled(false);
      }
    };

    // Check periodically
    const interval = setInterval(checkAutoReturn, 1000);

    return () => clearInterval(interval);
  }, [artRevealed, isUserControlled]);

  // 🎭 DEV: Navigate temperaments
  const nextTemperament = useCallback(() => {
    setTemperamentIndex(prev => (prev + 1) % TEMPERAMENTS.length);
  }, []);

  const prevTemperament = useCallback(() => {
    setTemperamentIndex(prev => (prev - 1 + TEMPERAMENTS.length) % TEMPERAMENTS.length);
  }, []);

  // Calculate final box transform (physics + creature expression)
  const creatureOffset = isDragging ? { x: 0, y: 0, z: 0 } : {
    x: expression.rotation.x + 20, // Offset from base
    y: expression.rotation.y + 25,
    z: expression.rotation.z,
  };

  const finalTransform = `
    rotateX(${physicsRotation.x + (isDragging ? 0 : creatureOffset.x * 0.3)}deg)
    rotateY(${physicsRotation.y + (isDragging ? 0 : creatureOffset.y * 0.3)}deg)
    rotateZ(${physicsRotation.z + (isDragging ? 0 : creatureOffset.z)}deg)
    translateX(${isDragging ? 0 : expression.translation.x}px)
    translateY(${isDragging ? 0 : expression.translation.y}px)
    scale(${expression.scale})
  `;

  // WebSocket connection
  useEffect(() => {
    if (notificationsEnabled && !wsConnectedRef.current) {
      const section = seatInfo?.section || 'A1';
      const conn = createPixelConnection({
        serverUrl: PIXEL_SERVER_URL,
        section,
        seatInfo: seatInfo ? { section: seatInfo.section, row: seatInfo.row, number: seatInfo.number } : undefined,
        onStateChange: (state) => setWsState(state),
        onEvent: (event) => {
          if (event.type === 'BUZZ') window.dispatchEvent(new CustomEvent('pixel-buzz'));
          else if (event.type === 'COLOR') window.dispatchEvent(new CustomEvent('pixel-color', { detail: { color: event.color, duration: event.duration } }));
          else if (event.type === 'CLEAR') window.dispatchEvent(new CustomEvent('pixel-clear'));
        },
        onError: (error) => console.error(`[FunPage] Connection error:`, error),
      });
      conn.connect();
      setWsConnection(conn);
      wsConnectedRef.current = true;
    }
    return () => {
      if (wsConnection && !notificationsEnabled) {
        wsConnection.disconnect();
        setWsConnection(null);
        wsConnectedRef.current = false;
      }
    };
  }, [notificationsEnabled, seatInfo]);

  useEffect(() => {
    return () => { if (wsConnection) wsConnection.disconnect(); };
  }, []);

  // Wake Lock
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        const lock = await (navigator as any).wakeLock.request('screen');
        setWakeLock(lock);
      }
    } catch (err) { console.log('Wake Lock not available:', err); }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLock) { wakeLock.release(); setWakeLock(null); }
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }, [wakeLock]);

  const enterFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) elem.requestFullscreen().catch(() => {});
  }, []);

  const vibratePhone = useCallback(() => {
    if ('vibrate' in navigator) navigator.vibrate([200, 100, 200, 100, 200]);
  }, []);

  // 🔊 SUB-BASS AUDIO "VIBRATION" - You can FEEL this through speakers!
  const audioContextRef = useRef<AudioContext | null>(null);
  const playSubBassBuzz = useCallback(() => {
    try {
      // Create or reuse AudioContext
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioContextRef.current;

      // Resume if suspended (iOS requires user interaction)
      if (ctx.state === 'suspended') ctx.resume();

      // 3x BUZZ pattern with sub-bass (40Hz = you FEEL it!)
      const buzzPattern = [
        { start: 0, duration: 0.15 },
        { start: 0.25, duration: 0.15 },
        { start: 0.5, duration: 0.15 },
      ];

      buzzPattern.forEach(({ start, duration }) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.value = 40; // Sub-bass - physically felt!

        gainNode.gain.setValueAtTime(0, ctx.currentTime + start);
        gainNode.gain.linearRampToValueAtTime(1, ctx.currentTime + start + 0.02);
        gainNode.gain.linearRampToValueAtTime(1, ctx.currentTime + start + duration - 0.02);
        gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + start + duration);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.start(ctx.currentTime + start);
        oscillator.stop(ctx.currentTime + start + duration);
      });

      console.log('🔊 Sub-bass buzz played!');
    } catch (err) {
      console.warn('Audio not available:', err);
    }
  }, []);

  const startPixelSequence = useCallback((color: string, reward: string) => {
    vibratePhone();
    setShowBrightnessPrompt(true);
    setCountdown(3);
    setLastReward(reward);
    requestWakeLock();
    enterFullscreen();
    
    document.body.style.backgroundColor = '#ffffff';
    setTimeout(() => { document.body.style.backgroundColor = ''; }, 500);
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setShowBrightnessPrompt(false);
          setPhoneColor(color);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [vibratePhone, requestWakeLock, enterFullscreen]);

  // Flash state for visual feedback (especially iOS which can't vibrate)
  const [flashActive, setFlashActive] = useState(false);

  // Listen for pixel events
  useEffect(() => {
    const handleBuzz = () => {
      console.log('🚨 BUZZ RECEIVED! Triggering all feedback...');

      // 1. Try vibration (Android only, silent fail on iOS)
      vibratePhone();

      // 2. 🔊 SUB-BASS AUDIO - You FEEL this through speakers!
      playSubBassBuzz();

      // 3. VISUAL FLASH - Works everywhere!
      let flashCount = 0;
      const flashInterval = setInterval(() => {
        if (flashCount >= 6) { // 3 flashes (on/off = 2 each)
          clearInterval(flashInterval);
          setFlashActive(false);
          return;
        }
        setFlashActive(flashCount % 2 === 0);
        flashCount++;
      }, 150);
    };

    const handleFlash = (e: CustomEvent) => {
      const { active } = e.detail;
      setFlashActive(active);
    };

    const handleColor = (e: CustomEvent) => {
      const { color, duration } = e.detail;
      setPhoneColor(color);
      setParticipated(true);
      if (duration) setTimeout(() => setPhoneColor(null), duration);
    };
    const handleClear = () => { setPhoneColor(null); releaseWakeLock(); };

    window.addEventListener('pixel-buzz', handleBuzz as EventListener);
    window.addEventListener('pixel-flash', handleFlash as EventListener);
    window.addEventListener('pixel-color', handleColor as EventListener);
    window.addEventListener('pixel-clear', handleClear as EventListener);

    return () => {
      window.removeEventListener('pixel-buzz', handleBuzz as EventListener);
      window.removeEventListener('pixel-flash', handleFlash as EventListener);
      window.removeEventListener('pixel-color', handleColor as EventListener);
      window.removeEventListener('pixel-clear', handleClear as EventListener);
    };
  }, [vibratePhone, playSubBassBuzz, releaseWakeLock]);

  // Mystery box reveal
  const handleRevealClick = useCallback(() => {
    if (artRevealing || artRevealed) return;
    setArtRevealing(true);
    vibratePhone();
    setTimeout(() => { setArtRevealing(false); setArtRevealed(true); }, 1500);
  }, [artRevealing, artRevealed, vibratePhone]);

  // Glow styling based on mood
  const isIntenseMood = mood === 'rage' || mood === 'agitated';
  const glowStyle = {
    boxShadow: isIntenseMood 
      ? `0 0 ${30 + energy * 0.3}px ${expression.glowColor}, inset 0 0 20px rgba(255,100,100,0.3)`
      : `0 0 ${10 + energy * 0.1}px ${expression.glowColor}`,
  };

  return (
    <div className={styles.funPage}>
      {/* Phone color overlay */}
      {phoneColor && (
        <div className={styles.colorOverlay} style={{ backgroundColor: phoneColor }} onClick={() => setPhoneColor(null)}>
          <div className={styles.colorInstructions}>
            <span>📱 Hold phone UP!</span>
            <span className={styles.tapHint}>Tap to exit</span>
          </div>
        </div>
      )}

      {/* Brightness prep overlay */}
      {showBrightnessPrompt && (
        <div className={styles.brightnessOverlay}>
          <div className={styles.brightnessContent}>
            <span className={styles.brightnessIcon}>🔆</span>
            <span className={styles.brightnessText}>MAX BRIGHTNESS!</span>
            <span className={styles.countdownNumber}>{countdown}</span>
          </div>
        </div>
      )}

      {/* Flash overlay for BUZZ visual feedback (especially iOS) */}
      {flashActive && (
        <div className={styles.flashOverlay} />
      )}

      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ChevronRight style={{ transform: 'rotate(180deg)' }} size={24} />
        </button>
        <h1>People Pixels</h1>
        <div className={styles.connectionStatus}>
          {wsState === 'connected' ? <Wifi size={20} className={styles.connected} /> : <WifiOff size={20} className={styles.disconnected} />}
        </div>
      </header>

      <main className={styles.content}>
        {/* 🏟️ HERO SECTION - Stadium bird's eye view */}
        <section className={styles.heroSection}>
          <div
            className={styles.heroImageContainer}
            onClick={() => setShowStadiumLightbox(true)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src="/peoplepixels.jpg"
              alt="People Pixels - Stadium lit up by phones"
              className={styles.heroImage}
            />
            <div className={styles.heroOverlay}>
              <img src="/fmlogo.png" alt="Frosty Mango" className={styles.heroLogo} />
              <h2 className={styles.heroTitle}>People Pixels</h2>
              <p className={styles.heroSubtitle}>Be part of the magic tonight!</p>
            </div>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatIcon}>📱</span>
              <span className={styles.heroStatValue}>12,847</span>
              <span className={styles.heroStatLabel}>Phones Ready</span>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatIcon}>🎨</span>
              <span className={styles.heroStatValue}>3</span>
              <span className={styles.heroStatLabel}>Pixel Events</span>
            </div>
          </div>
        </section>

        {/* 🌪️ INTERACTIVE MYSTERY BOX WITH PHYSICS 🌪️ */}
        <section className={styles.mysterySection}>
          {/* 🎭 DEV NAVIGATION ARROWS */}
          {DEV_MODE && !artRevealed && (
            <div className={styles.devNavigation}>
              <button 
                className={styles.devArrow} 
                onClick={prevTemperament}
                disabled={TEMPERAMENTS.length <= 1}
              >
                <ChevronLeft size={24} />
              </button>
              <span className={styles.devLabel}>
                {currentTemperament.emoji} {currentTemperament.name}
              </span>
              <button 
                className={styles.devArrow} 
                onClick={nextTemperament}
                disabled={TEMPERAMENTS.length <= 1}
              >
                <ChevronRight size={24} />
              </button>
            </div>
          )}

          <div 
            ref={boxRef}
            className={`${styles.previewBanner} ${artRevealing ? styles.revealing : ''} ${isDragging ? styles.dragging : ''}`}
            onClick={artRevealed ? handleRevealClick : undefined}
            onPointerDown={!artRevealed ? handlePointerDown : undefined}
            onPointerMove={!artRevealed ? handlePointerMove : undefined}
            onPointerUp={!artRevealed ? handlePointerUp : undefined}
            onPointerLeave={!artRevealed ? handlePointerUp : undefined}
            onPointerCancel={!artRevealed ? handlePointerUp : undefined}
            style={{ touchAction: 'none' }}
          >
            {!artRevealed ? (
              <div
                key={ragePhase || 'normal'} // Force remount when rage phase changes to restart animations!
                className={`
                  ${styles.mysteryBox}
                  ${isIntenseMood ? styles.intense : ''}
                  ${ragePhase === 'intro' ? styles.rageIntro : ''}
                  ${ragePhase === 'shake' ? styles.rageShake : ''}
                  ${ragePhase === 'outro' ? styles.rageOutro : ''}
                  ${!isDragging && !isUserControlled && mood === 'sleeping' && energy < 20 ? styles.idleBreathing : ''}
                `}
                style={{
                  // During rage phases, let CSS animations handle the transform!
                  transform: ragePhase ? undefined : ((!isDragging && !isUserControlled && mood === 'sleeping' && energy < 20) ? undefined : finalTransform),
                  transition: isDragging ? 'none' : (ragePhase ? 'none' : `transform ${expression.transitionMs}ms ease-out`),
                  ...glowStyle,
                }}
              >
                {/* 3D Box Faces - ALL 6 SIDES! */}
                <div className={styles.boxFront}>
                  {/* Devil bulge indicator */}
                  <div 
                    className={styles.devilBulge}
                    style={{
                      left: `${bulge.x}%`,
                      top: `${bulge.y}%`,
                      transform: `translate(-50%, -50%) scale(${bulge.scale})`,
                      opacity: bulge.opacity,
                    }}
                  />
                  
                  {/* Content */}
                  <span className={`${styles.lockIcon} ${mood === 'rage' ? styles.lockRage : ''}`}>
                    {indicators.emoji}
                  </span>
                  <span className={`${styles.mysteryText} ${mood === 'rage' ? styles.textRage : ''}`}>
                    {indicators.text}
                  </span>
                  
                  {/* Energy bar */}
                  <div className={styles.energyBar}>
                    <div 
                      className={styles.energyFill} 
                      style={{ 
                        width: `${energy}%`,
                        backgroundColor: energy > 70 ? '#ff6b6b' : energy > 30 ? '#ffd93d' : '#6bcb77',
                      }} 
                    />
                  </div>
                </div>
                <div className={styles.boxBack} />
                <div className={styles.boxTop} />
                <div className={styles.boxBottom} />
                <div className={styles.boxRight} />
                <div className={styles.boxLeft} />
              </div>
            ) : (
              <div className={styles.revealedArt}>
                <div className={styles.artContent}>
                  <Sparkles size={48} className={styles.sparkleIcon} />
                  <span className={styles.artEmoji}>🎨</span>
                  <span className={styles.artTitle}>Tonight's Secret Art</span>
                  <span className={styles.artSubtitle}>Cowboys vs Dragons</span>
                </div>
              </div>
            )}
            
            {/* Interaction feedback */}
            {interactionFeedback && (
              <div className={styles.interactionFeedback}>
                {interactionFeedback}
              </div>
            )}
          </div>
          
          <p className={styles.mysteryLabel}>
            {artRevealed ? '🎉 REVEALED!' : (isDragging ? '🎮 YOU CONTROL IT!' : indicators.label)}
          </p>
          
          {!artRevealed && (
            <p className={styles.interactionHint}>
              {isDragging
                ? 'Release to spin!'
                : !isUserControlled && mood === 'sleeping' && energy < 20
                  ? '💤 Something is resting... tap to wake it?'
                  : !isUserControlled && mood === 'sleeping'
                    ? '✨ It stirs... touch to interact!'
                    : '👆 Drag to spin • Flick for momentum!'}
            </p>
          )}
        </section>

        {/* Notifications toggle */}
        <section className={styles.notificationSection}>
          <div className={styles.notificationCard}>
            <div className={styles.notificationInfo}>
              {notificationsEnabled ? <BellRing size={24} className={styles.bellActive} /> : <Bell size={24} />}
              <div>
                <h3>Pixel Alerts</h3>
                <p>{notificationsEnabled ? 'Ready for events!' : 'Get notified when pixels start'}</p>
              </div>
            </div>
            <button 
              className={`${styles.toggleButton} ${notificationsEnabled ? styles.active : ''}`}
              onClick={() => setNotificationsEnabled(!notificationsEnabled)}
            >
              {notificationsEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
          
          {participated && lastReward && (
            <div className={styles.rewardBanner}>
              <Gift size={20} />
              <span>You earned: {lastReward}</span>
            </div>
          )}
        </section>

        {/* Events */}
        <section className={styles.eventsSection}>
          <div className={styles.sectionHeader}>
            <Calendar size={20} />
            <h2>Tonight's Pixels</h2>
          </div>
          
          <div className={styles.eventsList}>
            {mockEvents.map((event) => (
              <div key={event.id} className={styles.eventCard}>
                <div className={styles.eventPreview}>{event.imagePreview}</div>
                <div className={styles.eventDetails}>
                  <h3>{event.name}</h3>
                  <p className={styles.eventTime}>{event.time}</p>
                  <p className={styles.eventReward}>🎁 {event.reward}</p>
                </div>
                <div className={styles.eventMode}>
                  {event.mode === 'phone_up' ? '📱' : '🧍'}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Preview button */}
        <button className={styles.previewButton} onClick={() => setShowPreview(true)}>
          <ZoomIn size={20} />
          Preview How It Works
        </button>

        {/* Preview modal */}
        {showPreview && (
          <div className={styles.previewModal} onClick={() => setShowPreview(false)}>
            <div className={styles.previewContent} onClick={(e) => e.stopPropagation()}>
              <button className={styles.closePreview} onClick={() => setShowPreview(false)}>
                <X size={24} />
              </button>
              <h2>How People Pixels Works</h2>
              <div className={styles.previewSteps}>
                <div className={styles.step}>
                  <span className={styles.stepNumber}>1</span>
                  <Bell size={32} />
                  <p>Enable alerts & your phone buzzes when a pixel event starts</p>
                </div>
                <div className={styles.step}>
                  <span className={styles.stepNumber}>2</span>
                  <Users size={32} />
                  <p>Follow the instructions - hold up your phone or stand/sit</p>
                </div>
                <div className={styles.step}>
                  <span className={styles.stepNumber}>3</span>
                  <Sparkles size={32} />
                  <p>Together we create amazing images across the stadium!</p>
                </div>
              </div>
              <button className={styles.testButton} onClick={() => startPixelSequence('#ff0000', 'Test reward!')}>
                🧪 Test Color Flash
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Stadium Lightbox */}
      {showStadiumLightbox && (
        <div className={styles.lightboxOverlay} onClick={() => setShowStadiumLightbox(false)}>
          <button
            className={styles.lightboxClose}
            onClick={() => setShowStadiumLightbox(false)}
          >
            ✕
          </button>
          <img
            src="/peoplepixels.jpg"
            alt="Stadium People Pixels - Full View"
            className={styles.lightboxImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
